import { useState } from 'react';

import { logger } from '@azure/storage-blob';
import {
  Box,
  FormHelperText,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { AddressApi, MileMarkerApi } from '@/api';
import { IcClose, IcGoogle, IcMileMarker } from '@/assets/images';
import { IAddress, IAddressForm, IMileMarker, PointType } from '@/models';
import { getAddressLocation, getBusinessNamePrefixedText } from '@/services';
import { googlePlaceService } from '@/services/service.google';
import { useToastStore } from '@/store';

import { AddressOptionType, AddressSearch } from './AddressSearch';
import { BaseFieldProps } from '../../FormElements';

interface AddressItemProps extends BaseFieldProps {
  name?: string;
  showMileMarkerOption?: boolean;
  showTruncatedTooltip?: boolean;
  enableBusinessName?: boolean;
  value?: AddressOptionType | null;
  pendingAddress?: IAddressForm | null;
  overrideUpdateAddress?: (newAddress: IAddressForm | null) => void;
  locationRestriction?: google.maps.LatLngBoundsLiteral;
  locationBias?: string | google.maps.LatLngLiteral | google.maps.CircleLiteral;
  updateCurrentAddress: (newAddress: IAddressForm | null) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  onAddressSelectionStart?: (previewAddress: IAddressForm) => void;
  onAddressSelectionError?: () => void;
}

const validateCoordinates = (
  input: string,
): { lat: number; lng: number } | null => {
  // Clean input and handle flexible spacing
  const cleaned = input.replace(/\s+/g, '').trim();
  const parts = cleaned.split(',');

  if (parts.length !== 2) return null;

  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);

  if (isNaN(lat) || isNaN(lng)) return null;

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

  return { lat, lng };
};

export const AddressItem = (props: AddressItemProps) => {
  const {
    value,
    error,
    label,
    required,
    locationRestriction,
    locationBias,
    updateCurrentAddress,
    overrideUpdateAddress,
    disabled,
    showMileMarkerOption,
    showTruncatedTooltip,
    enableBusinessName,
    autoFocus,
    onAddressSelectionStart,
    onAddressSelectionError,
  } = props;
  const { updateToast } = useToastStore();
  const [options, setOptions] = useState<AddressOptionType[]>([]);
  const [mileMarkers, setMileMarkers] = useState<IMileMarker[]>([]);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState<'google' | 'milemarker'>('google');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCoordinateSearch = async (coords: {
    lat: number;
    lng: number;
  }) => {
    try {
      setLoading(true);
      const place = await googlePlaceService.geocodeCoordinates(
        coords.lat,
        coords.lng,
      );
      if (place) {
        const option: AddressOptionType = {
          mainText: place.formatted_address || `${coords.lat}, ${coords.lng}`,
          secondaryText: 'Found from coordinates',
          placeId: `coords:${coords.lat},${coords.lng}`,
        };
        setOptions([option]);
        return;
      }

      // If no results found, still show the coordinates as an option
      const option: AddressOptionType = {
        mainText: `${coords.lat}, ${coords.lng}`,
        secondaryText: 'Click to use these coordinates',
        placeId: `coords:${coords.lat},${coords.lng}`,
      };
      setOptions([option]);
    } catch (err) {
      updateToast({ open: true, message: 'Error looking up coordinates' });
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const retrieveFullAddress = async (newValue: AddressOptionType) => {
    try {
      let addressDetails: IAddressForm | null = null;

      if (newValue.placeId === 'mileMarker') {
        const currentMileMarker =
          mileMarkers.find(
            (marker) => marker.addressLabel === newValue.mainText,
          ) || null;
        if (currentMileMarker) {
          addressDetails = {
            pointType: PointType.ROOF_TOP,
            addressNumber: currentMileMarker.addressNumber,
            addressLabel: currentMileMarker.addressLabel,
            completeStreetName: currentMileMarker.completeStreetName,
            state: currentMileMarker.state,
            zipName: currentMileMarker.zipName,
            zipcode: currentMileMarker.zipCode,
            point: currentMileMarker.point,
          };
        }
      } else if (newValue.placeId?.startsWith('coords:')) {
        const [lat, lng] = newValue.placeId
          .replace('coords:', '')
          .split(',')
          .map(Number);
        const place = await googlePlaceService.geocodeCoordinates(lat, lng);
        if (place) {
          addressDetails = googlePlaceService.getAddressDetails(place);
        } else {
          // If geocoding fails, create a basic address with just the coordinates
          addressDetails = {
            pointType: PointType.ROOF_TOP,
            addressLabel: `${lat}, ${lng}`,
            point: {
              type: 'Point',
              coordinates: [lng, lat],
            },
          };
        }
      } else {
        addressDetails = await googlePlaceService.getPlaceDetails(
          String(newValue.placeId),
        );
      }

      if (addressDetails) {
        if (addressDetails.placeType === 'INTERSECTION') {
          addressDetails.addressNumber = undefined;
          addressDetails.completeStreetName = undefined;
        }
        await addNewAddress(addressDetails);
      }
    } catch (err: any) {
      onAddressSelectionError?.();
      updateToast({ open: true, message: err.message });
    }
  };

  const buildPreviewAddress = (option: AddressOptionType): IAddressForm => {
    const addressLabel = [option.mainText, option.secondaryText]
      .filter(Boolean)
      .join(', ');

    return {
      addressLabel: addressLabel || option.mainText || '',
      normalized: addressLabel || option.mainText || undefined,
    } as IAddressForm;
  };

  const addNewAddress = async (newAddress: IAddressForm) => {
    try {
      // The Google-derived business name is a per-CFS attribute; keep it off
      // the shared master address record and re-attach it for the form value.
      const { businessName, ...payload } = newAddress;
      const res = await AddressApi.createAddress(payload as IAddressForm);
      const created = enableBusinessName
        ? { ...res.data, businessName: businessName ?? res.data.businessName }
        : res.data;
      if (overrideUpdateAddress) {
        overrideUpdateAddress(created);
      } else {
        updateCurrentAddress(created);
      }
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      throw err;
    }
  };

  const onSearch = async (inputValue: string) => {
    try {
      if (!inputValue) {
        setOptions([]);
        return;
      }

      if (inputValue === '~' && showMileMarkerOption) {
        setProvider('milemarker');
      }

      if (inputValue === '*') {
        setProvider('google');
      }

      setLoading(true);
      // Check if input might be coordinates
      const coords = validateCoordinates(inputValue);
      if (coords && provider === 'google') {
        await handleCoordinateSearch(coords);
        return;
      }

      if (provider === 'milemarker') {
        const searchParameter = inputValue.replace('~', '');
        const res = await MileMarkerApi.list({
          search: searchParameter,
        });
        const newOptions = res.data.map((marker) => ({
          mainText: marker.addressLabel,
          secondaryText: `${marker.completeStreetName}, ${marker.state}, ${marker.zipCode}`,
          placeId: 'mileMarker',
        }));
        setMileMarkers([...res.data]);
        setOptions(newOptions);
      } else {
        const searchParameter = inputValue.replace('*', '');
        const res = await googlePlaceService.getPlacePredictions(
          searchParameter,
          locationRestriction,
          locationBias,
        );
        const newOptions = res.map(({ structured_formatting, place_id }) => ({
          mainText: structured_formatting.main_text,
          secondaryText: structured_formatting.secondary_text,
          placeId: place_id,
        }));
        setOptions(newOptions);
      }
    } catch (err: any) {
      logger.error('Search error:', err);
      updateToast({ open: true, message: err.message });
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (newValue: string | AddressOptionType | null) => {
    if (!!newValue && typeof newValue !== 'string') {
      onAddressSelectionStart?.(buildPreviewAddress(newValue));
      retrieveFullAddress(newValue);
    } else if (overrideUpdateAddress && newValue === '') {
      overrideUpdateAddress('' as IAddressForm);
    }
  };

  const handleInputChange = (newValue: string) => {
    if (newValue.length > 0) {
      onSearch(newValue);
    } else {
      setMileMarkers([]);
      setOptions([]);
      if (!overrideUpdateAddress) {
        updateCurrentAddress(null);
      }
    }
  };

  const getInputText = (option: AddressOptionType | string) => {
    if (typeof option === 'string') {
      return option;
    }

    const { mainText, secondaryText } = option || {};
    if (mainText && secondaryText) {
      return `${mainText} ${secondaryText}`;
    }

    // The sub-address has its own field next to this input, so keep it out of
    // the Address text — otherwise a unit like "# 212" shows in both places.
    const base = getAddressLocation(option as IAddress, true);
    return enableBusinessName
      ? getBusinessNamePrefixedText((option as IAddress)?.businessName, base)
      : base;
  };

  const checkOptionEqualToValue = (
    option: string | AddressOptionType,
    newValue: string | AddressOptionType,
  ): boolean => {
    // Handle string comparisons
    if (typeof option === 'string' || typeof newValue === 'string') {
      return option === newValue;
    }

    // Handle null/undefined cases
    if (!option || !newValue) {
      return !option && !newValue;
    }

    // Case 1: Both have _id (stored addresses) - compare by ID
    if (option._id && newValue._id) {
      return option._id === newValue._id;
    }

    // Case 2: Option is from Google search (has mainText), value is stored address (has addressLabel)
    if (option.mainText && newValue.addressLabel) {
      return option.mainText === newValue.addressLabel;
    }

    // Case 3: Both have addressLabel (two stored addresses without _id)
    if (option.addressLabel && newValue.addressLabel) {
      return option.addressLabel === newValue.addressLabel;
    }

    // Case 4: Option is stored address (has addressLabel), value is from Google search (has mainText)
    if (option.addressLabel && newValue.mainText) {
      return option.addressLabel === newValue.mainText;
    }

    // Fallback: no match
    return false;
  };

  const theme = useTheme();
  const renderOption = (option: AddressOptionType) => {
    const { mainText, secondaryText } = option;
    return (
      <Stack sx={{ ml: 1 }}>
        <Typography variant="subtitle2" color={theme.palette.text.primary}>
          {mainText}
        </Typography>
        <Typography variant="body2">{secondaryText}</Typography>
      </Stack>
    );
  };

  const clearCurrentAddress = () => {
    setOptions([]);
    setMileMarkers([]);

    if (overrideUpdateAddress) {
      overrideUpdateAddress('' as IAddressForm);
      return;
    }

    updateCurrentAddress(null);
  };

  const handleClearAddress = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    clearCurrentAddress();
  };

  return (
    <Stack>
      <InputLabel>
        {label || 'Address'}
        {required && <span style={{ color: 'red', opacity: 0.8 }}> *</span>}
      </InputLabel>
      <Box
        sx={{
          position: 'relative',
          opacity: disabled ? 0.5 : 1,
          '& .address-clear-button': {
            opacity: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
            transition: 'opacity 0.15s ease',
          },
          '&:hover .address-clear-button, &:focus-within .address-clear-button':
            {
              opacity: 1,
              visibility: 'visible',
              pointerEvents: disabled ? 'none' : 'auto',
            },
        }}
      >
        <AddressSearch
          filterOptions={(filterOptions) => filterOptions}
          options={options}
          value={value}
          onChange={(_, selected) => {
            handleChange(selected);
          }}
          overridePlaceClick={!!overrideUpdateAddress}
          handleInputChange={handleInputChange}
          showTruncatedTooltip={showTruncatedTooltip}
          getOptionLabel={getInputText}
          renderOption={renderOption}
          isOptionEqualToValue={checkOptionEqualToValue}
          loading={loading}
          disabled={disabled}
          autoFocus={autoFocus}
          disableClearable={true}
          sx={{
            '& .MuiOutlinedInput-root': {
              paddingRight: value ? '76px !important' : '44px !important',
            },
          }}
        />
        {!!value && (
          <IconButton
            aria-label="Clear address"
            className="address-clear-button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClearAddress}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              color: theme.palette.error.main,
              width: 28,
              height: 28,
              padding: 0,
            }}
            disabled={disabled}
          >
            <IcClose />
          </IconButton>
        )}
        <IconButton
          id="demo-customized-button"
          aria-controls={open ? 'demo-customized-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          sx={{
            position: 'absolute',
            right: 36,
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            width: 28,
            height: 28,
            padding: 0,
          }}
          disabled={disabled}
        >
          {provider === 'google' ? <IcGoogle /> : <IcMileMarker />}
        </IconButton>
        {showMileMarkerOption && (
          <Menu
            id="provider-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            sx={{ textAlign: 'left' }}
          >
            <MenuItem
              sx={{ justifyContent: 'flex-start', alignItems: 'center' }}
              onClick={() => {
                setProvider('google');
                if (value) {
                  clearCurrentAddress();
                }
                handleClose();
              }}
            >
              <IcGoogle />
              <Typography sx={{ marginLeft: 2 }}>
                Google - Default (*)
              </Typography>
            </MenuItem>
            <MenuItem
              sx={{ justifyContent: 'flex-start' }}
              onClick={() => {
                setProvider('milemarker');
                if (value) {
                  clearCurrentAddress();
                }
                handleClose();
              }}
            >
              <IcMileMarker />
              <Typography sx={{ marginLeft: 2 }}>Mile Marker (~)</Typography>
            </MenuItem>
          </Menu>
        )}
      </Box>
      <FormHelperText error>{error}</FormHelperText>
    </Stack>
  );
};
